import React from 'react';
import {get} from '../../utils/request'
import { Spinner } from 'react-bootstrap';
import Alert from "../../component/Alert/Alert";
import Badge from "../../component/Badge/Badge";
import Button from "../../component/Button/Button";
import Layout from "../../component/Layout/Layout";
import "../../component/Table/Table.scss"
import {faEdit, faUserShield} from "@fortawesome/free-solid-svg-icons";

class Access extends React.Component {
    constructor(props) {
        super(props);
        const locationState = props.location.state || {};
        this.state = {
            roles: [],
            s3KeyCounts: {},
            errorMessage: "",
            successMessage: locationState.successMessage || "",
            loading: true,
            loadingSuccess: false
        };
        this.loadData = this.loadData.bind(this);
    }

    async loadData() {
        const [res, err] = await get(this, "/auth/role");
        if (err) {
            this.setState({errorMessage: "error: " + err, loading: false});
            return
        }
        const roles = res.data.Roles || [];
        this.setState({roles: roles, loading: false, loadingSuccess: true});

        const s3KeyCounts = {};
        await Promise.all(roles.map(async (role) => {
            const [keysRes, keysErr] = await get(this, "/auth/role/" + role.Id + "/s3Key");
            if (!keysErr && keysRes.data.S3Keys) {
                const keys = keysRes.data.S3Keys;
                const total = keys.length;
                const inactive = keys.filter(k => k.Status === 'inactive').length;
                s3KeyCounts[role.Id] = { total, inactive };
            } else {
                s3KeyCounts[role.Id] = { total: 0, inactive: 0 };
            }
        }));
        this.setState({s3KeyCounts: s3KeyCounts});
    }

    formatS3Keys(roleId) {
        const counts = this.state.s3KeyCounts[roleId];
        if (!counts) return '-';
        if (counts.total === 0) return <Badge className="badge-danger-outline" label="0"/>;
        if (counts.inactive > 0) {
            return `${counts.total} (${counts.inactive} inactive)`;
        }
        return `${counts.total}`;
    }

    componentDidMount() {
        this.loadData();
        // Clear location state so message doesn't show on refresh
        if (this.props.history.location.state) {
            this.props.history.replace({ pathname: '/access', state: {} });
        }
    }

    formatAccess(role) {
        const lines = [];

        const yesBadge = <Badge className="badge-success-outline" label="yes"/>;
        const noBadge = <Badge className="badge-cancel-outline" label="no"/>;

        lines.push(<div key="admin">Admin access: {role.BucketAdmin ? yesBadge : noBadge}</div>);
        lines.push(<div key="create">Can create buckets: {role.BucketCreate ? yesBadge : noBadge}</div>);
        lines.push(<div key="delete">Can delete buckets: {role.BucketDelete ? yesBadge : noBadge}</div>);

        if (role.BucketAccessAll) {
            lines.push(<div key="buckets">Bucket access: <Badge className="badge-success-outline" label="all buckets"/></div>);
        } else {
            const bucketPermissions = role.BucketPermissions;
            if (!bucketPermissions || bucketPermissions.length === 0) {
                lines.push(<div key="buckets">Bucket access: <Badge className="badge-danger-outline" label="none"/></div>);
            } else {
                const formatPermissions = (bp) => {
                    const perms = [];
                    if (bp.List) perms.push('List');
                    if (bp.Read) perms.push('Read');
                    if (bp.Write) perms.push('Write');
                    if (bp.Delete) perms.push('Delete');
                    if (perms.length === 4) return 'Full';
                    return perms.join(', ');
                };

                const byLocation = {};
                bucketPermissions.forEach(bp => {
                    if (!byLocation[bp.LocationUid]) {
                        byLocation[bp.LocationUid] = [];
                    }
                    byLocation[bp.LocationUid].push({
                        name: bp.BucketName,
                        permissions: formatPermissions(bp)
                    });
                });

                lines.push(<div key="buckets-header">Bucket access: <Badge className="badge-warning-outline" label="limited"/></div>);
                Object.keys(byLocation).sort().forEach(location => {
                    const buckets = byLocation[location];
                    const bucketDetails = buckets.map((b, i) => (
                        <span key={i}>{b.name} ({b.permissions})</span>
                    ));
                    lines.push(
                        <div key={location} style={{marginLeft: '10px'}}>
                            <strong>{location}:</strong> {bucketDetails.reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, ", ", curr], [])}
                        </div>
                    );
                });
            }
        }

        return lines;
    }

    render() {
        const { roles, errorMessage, successMessage, loading, loadingSuccess } = this.state;
        const { history } = this.props;

        const homepageUrl = `${process.env.REACT_APP_HOMEPAGE_URL}`;

        let roleRows = [];
        if (roles && roles.length > 0) {
            roleRows = roles.map((role) =>
                <tr key={role.Id}>
                    <td data-label="Name" style={{verticalAlign: 'top'}}>
                        <span
                            className="link-style"
                            style={{cursor: 'pointer', color: '#007bff', textDecoration: 'underline'}}
                            onClick={() => history.push('/access/role/' + role.Id)}
                        >
                            {role.Name}
                        </span>
                    </td>
                    <td data-label="Status" style={{verticalAlign: 'top'}}>
                        {role.Status === 'active'
                            ? <Badge className="badge-success-outline" label="active"/>
                            : <Badge className="badge-danger-outline" label="inactive"/>}
                    </td>
                    <td data-label="Access" style={{verticalAlign: 'top'}}>{this.formatAccess(role)}</td>
                    <td data-label="S3 Keys" style={{verticalAlign: 'top'}}>{this.formatS3Keys(role.Id)}</td>
                    <td style={{verticalAlign: 'top'}}>
                        <Button
                            className="primary-outline btn btn-md"
                            faIcon={faEdit}
                            label="Properties"
                            onClick={() => history.push('/access/role/' + role.Id)}/>
                    </td>
                </tr>
            );
        }

        return <Layout activeMenu="/access" isLoggedIn={true}>
            <h1>Access</h1>
            {loading &&
            <>
                <Spinner animation="border" variant="primary"/> Loading roles...
                <br/><br/>
            </>
            }
            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            {loadingSuccess &&
            <>
                <div className="d-flex justify-content-between align-items-center">
                    <h2 className="mb-0">Roles</h2>
                    <Button className="primary btn btn-margin" faIcon={faUserShield} label="Create new role" onClick={() => history.push('/access/role/create')}/>
                </div>
                {roleRows.length > 0 &&
                <>
                <table className="table-ctr">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Access</th>
                        <th>S3 Keys</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>{roleRows}</tbody>
                </table>
                </>
                }
                {roleRows.length === 0 &&
                <p>No roles created yet. Create your first role to get started.</p>
                }
                <br/>
                <Alert variant="info">
                    <b>For technical configurations, please consult our documentation</b> <br/>
                    <a href={homepageUrl+'/resources/s3-api-permissions-and-compatibility'} target="_blank" rel="noopener">S3 API permissions and compatibility</a>
                </Alert>
            </>
            }
        </Layout>;
    }
}

export default Access
